import React, { useState } from 'react';
import { IoMdPlay, IoMdSearch } from 'react-icons/io';
import { SoundResult, searchSounds } from '../../../utils/soundSearch';
import { useThemeStore } from '../../../store/themeStore';

/**
 * myinstants.com media URLs don't send CORS headers, so the Web Audio
 * `fetch` in useAudio would fail. We wrap them through a free CORS proxy
 * so playback + decode work in the browser.
 */
const wrapForPlayback = (url: string): string => {
    if (!url) return url;
    if (url.includes('myinstants.com')) {
        return `https://corsproxy.io/?url=${encodeURIComponent(url)}`;
    }
    return url;
};

type SoundPickerProps = {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
};

const SoundPicker: React.FC<SoundPickerProps> = ({ value, onChange, placeholder }) => {
    const { selectedTheme } = useThemeStore();
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<SoundResult[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [open, setOpen] = useState(false);

    const handleSearch = async () => {
        if (!query.trim()) return;
        setLoading(true);
        setError(null);
        try {
            const found = await searchSounds(query.trim());
            setResults(found);
            setOpen(true);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Search failed');
            setResults([]);
        } finally {
            setLoading(false);
        }
    };

    const testPlay = async (url: string) => {
        try {
            const res = await fetch(wrapForPlayback(url));
            const arrayBuffer = await res.arrayBuffer();
            const AudioCtx =
                window.AudioContext ||
                (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
            const ctx = new AudioCtx();
            const buffer = await ctx.decodeAudioData(arrayBuffer);
            const src = ctx.createBufferSource();
            src.buffer = buffer;
            src.connect(ctx.destination);
            src.start(0);
        } catch (err) {
            console.error('Test play failed', err);
        }
    };

    return (
        <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
                <input
                    type="text"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder || 'Sound URL (myinstants.com)'}
                    className="w-full rounded border px-2 py-1 text-black text-sm"
                />
                {value && (
                    <button
                        type="button"
                        onClick={() => testPlay(value)}
                        title="Test this sound"
                        className="shrink-0 rounded p-1"
                        style={{ backgroundColor: `${selectedTheme.color.point}33` }}
                    >
                        <IoMdPlay size={16} />
                    </button>
                )}
            </div>

            <div className="flex items-center gap-2">
                <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            handleSearch();
                        }
                    }}
                    placeholder="Search myinstants.com…"
                    className="w-full rounded border px-2 py-1 text-black text-sm"
                />
                <button
                    type="button"
                    onClick={handleSearch}
                    disabled={loading}
                    title="Search sounds"
                    className="shrink-0 rounded p-1 disabled:opacity-50"
                    style={{ backgroundColor: `${selectedTheme.color.point}33` }}
                >
                    {loading ? <IoMdSearch size={16} className="animate-pulse" /> : <IoMdSearch size={16} />}
                </button>
            </div>

            {error && <p className="text-xs text-red-500">{error}</p>}

            {open && results.length > 0 && (
                <div className="relative">
                    <div className="absolute z-20 max-h-48 w-full overflow-y-auto rounded border bg-white text-black shadow-lg no-scrollbar">
                        {results.map((r) => (
                            <button
                                key={r.value}
                                type="button"
                                onClick={() => {
                                    // Store the CORS-wrapped URL so useAudio can fetch & decode it.
                                    onChange(wrapForPlayback(r.value));
                                    setOpen(false);
                                }}
                                className="flex w-full items-center justify-between gap-2 px-2 py-1 text-left text-sm hover:bg-black/10"
                            >
                                <span className="line-clamp-1 flex-1">{r.label}</span>
                                <span
                                    role="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        testPlay(r.value);
                                    }}
                                    className="shrink-0 rounded px-1"
                                >
                                    <IoMdPlay size={12} />
                                </span>
                            </button>
                        ))}
                    </div>
                    <button
                        type="button"
                        onClick={() => setOpen(false)}
                        className="fixed inset-0 z-10 -m-5 cursor-default"
                        aria-label="Close results"
                    />
                </div>
            )}
        </div>
    );
};

export default SoundPicker;
